import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const apiClient = axios.create({
  baseURL: API,
  timeout: 240000,
  headers: { "Content-Type": "application/json" },
});

export async function runAnalyze(payload) {
  const { data } = await apiClient.post("/analyze", payload);
  return data;
}

export async function runBatchAnalyze(payload) {
  const { data } = await apiClient.post("/batch_analyze", payload);
  return data;
}

export async function getHistory() {
  const { data } = await apiClient.get("/history");
  return data;
}

export async function getReport(id) {
  const { data } = await apiClient.get(`/report/${id}`);
  return data;
}

export async function deleteReport(id) {
  const { data } = await apiClient.delete(`/report/${id}`);
  return data;
}

export async function getBatches() {
  const { data } = await apiClient.get("/batches");
  return data;
}

export async function getBatch(id) {
  const { data } = await apiClient.get(`/batch/${id}`);
  return data;
}

export async function deleteBatch(id) {
  const { data } = await apiClient.delete(`/batch/${id}`);
  return data;
}

export function reportExportUrl(id, format) {
  return `${API}/report/${id}/export.${format}`;
}

export function batchExportUrl(id, format) {
  return `${API}/batch/${id}/export.${format}`;
}

// Shopify
export async function listShopifyConnections() {
  const { data } = await apiClient.get("/shopify/connections");
  return data;
}

export async function createShopifyConnection(payload) {
  const { data } = await apiClient.post("/shopify/connections", payload);
  return data;
}

export async function testShopifyConnection(id) {
  const { data } = await apiClient.post(`/shopify/connections/${id}/test`);
  return data;
}

export async function deleteShopifyConnection(id) {
  const { data } = await apiClient.delete(`/shopify/connections/${id}`);
  return data;
}

export async function listShopifyProductsPicker(id, { limit = 50, pageInfo = "", q = "" } = {}) {
  const params = { limit };
  if (pageInfo) params.page_info = pageInfo;
  if (q) params.q = q;
  const { data } = await apiClient.get(`/shopify/connections/${id}/products_picker`, { params });
  return data; // { products: [...], next_page_info: str|null }
}

export async function listShopifyProductScores(id) {
  const { data } = await apiClient.get(`/shopify/connections/${id}/product_scores`);
  return data; // { scores: { "<product_id>": { score, scanned_at } } }
}

export async function detectShopifyLanguage(id) {
  const { data } = await apiClient.get(`/shopify/connections/${id}/detect_language`);
  return data; // { detected, source, confidence, primary_locale, locale_normalized, products_detected, products_signal, samples_count }
}

export async function shopifyScan(payload) {
  // Backwards-compatible synchronous version (no progress). Prefer shopifyScanStart + poll.
  const { data } = await apiClient.post("/shopify/scan", payload);
  if (data && data.job_id && !data.resources) {
    // New API: returns a job; poll until done.
    return await pollScanJob(data.job_id);
  }
  return data;
}

export async function shopifyScanStart(payload) {
  const { data } = await apiClient.post("/shopify/scan", payload);
  return data; // { job_id, status }
}

export async function shopifyScanStatus(jobId) {
  const { data } = await apiClient.get(`/shopify/scan/status/${jobId}`);
  return data;
}

async function pollScanJob(jobId, onProgress) {
  while (true) {
    const s = await shopifyScanStatus(jobId);
    if (onProgress) onProgress(s);
    if (s.status === "done") return s.report;
    if (s.status === "error") {
      const err = new Error(s.error || "Scan failed");
      throw err;
    }
    await new Promise((r) => setTimeout(r, 2000));
  }
}

export async function shopifyScanWithProgress(payload, onProgress) {
  const start = await shopifyScanStart(payload);
  return await pollScanJob(start.job_id, onProgress);
}

export async function shopifyApply(payload) {
  const { data } = await apiClient.post("/shopify/apply", payload);
  return data;
}

export async function shopifyThemeScan({ connection_id, language = "en" }) {
  const { data } = await apiClient.post("/shopify/theme_scan", { connection_id, language });
  return data;
}

export async function shopifyThemeApply({ connection_id, items }) {
  const { data } = await apiClient.post("/shopify/theme_apply", { connection_id, items });
  return data;
}

export async function listApplyLog({ connection_id, limit = 50 } = {}) {
  const params = { limit };
  if (connection_id) params.connection_id = connection_id;
  const { data } = await apiClient.get("/shopify/apply_log", { params });
  return data;
}

export async function searchShopifyResources(connectionId, { resourceType = "product", q = "", text = "", limit = 20 } = {}) {
  const params = { resource_type: resourceType, q, limit };
  if (text) params.text = text;
  const { data } = await apiClient.get(`/shopify/connections/${connectionId}/resources`, { params });
  return data;
}

export async function applyShopifyRewrites(payload) {
  const { data } = await apiClient.post("/shopify/apply_rewrites", payload);
  return data;
}

export async function applyShopifyRewritesAuto(payload) {
  const { data } = await apiClient.post("/shopify/apply_rewrites_auto", payload);
  return data;
}

export async function generateCompliance(payload) {
  const { data } = await apiClient.post("/compliance/generate", payload);
  return data;
}

export async function pushComplianceToShopify(payload) {
  const { data } = await apiClient.post("/compliance/push_to_shopify", payload);
  return data;
}

export async function conditionAudit(payload) {
  const { data } = await apiClient.post("/shopify/condition/audit", payload, { timeout: 120000 });
  return data;
}

export async function conditionFix(payload) {
  const { data } = await apiClient.post("/shopify/condition/fix", payload);
  return data;
}

export async function conditionAutoFix(payload) {
  const { data } = await apiClient.post("/shopify/condition/auto_fix", payload, { timeout: 60000 });
  return data;
}

export async function conditionBulkAutoFix(payload) {
  const { data } = await apiClient.post("/shopify/condition/bulk_auto_fix", payload, { timeout: 180000 });
  return data;
}

export async function shopifyOAuthStatus() {
  const { data } = await apiClient.get("/shopify/oauth/status");
  return data;
}

export function shopifyOAuthInstallUrl(shop) {
  return `${API}/shopify/oauth/install?shop=${encodeURIComponent(shop)}`;
}

export async function rejectionHelper(payload) {
  const { data } = await apiClient.post("/rejection_helper", payload);
  return data;
}

export async function rejectionHistory(limit = 30) {
  const { data } = await apiClient.get("/rejection_helper", { params: { limit } });
  return data;
}

export async function getRejectionReport(id) {
  const { data } = await apiClient.get(`/rejection_helper/${id}`);
  return data;
}

export async function deleteRejectionReport(id) {
  const { data } = await apiClient.delete(`/rejection_helper/${id}`);
  return data;
}

export async function complianceHealthCheck(payload) {
  const { data } = await apiClient.post("/compliance/health_check", payload);
  return data;
}

export async function addPagesToFooterMenu(payload) {
  const { data } = await apiClient.post("/compliance/footer_menu", payload);
  return data;
}
